<template>
	<div ref="resultsWrapper" class="grid-container full">
		<div
			ref="resultsWrapperInner"
			aria-live="polite"
			:class="[
				'grid-x',
				'grid-margin-x',
				'grid-margin-y',
				'initialized',
				'dc-post-grid-results-inner',
				{
					loading: loading,
				},
			]"
		>
			<no-results v-if="results.length === 0" :text="dctxt('noResultsMessage')" />
			<div
				v-for="(result, index) in results"
				ref="resultsWrapperInnerAlt"
				:key="'result-' + index"
				:class="['cell', gridClasses]"
			>
				<course-card
					v-if="postType === 'dc_course'"
					:excerpt="result.excerpt"
					:id="result.id"
					:label="result.label"
					:title="result.title"
					:date="result.course_date"
					:url="result.url"
				/>
				<post-card
					v-else-if="postType === 'dc_course_overview'"
					:excerpt="result.excerpt"
					:id="result.id"
					:image="result.image"
					:label="result.label"
					:suptitle="result.suptitle"
					:title="result.title"
					:url="result.url"
				/>
				<location-card
					v-else-if="postType === 'dc_location'"
					:address="result.address"
					:email="result.email"
					:id="result.id"
					:phone="result.phone"
					:title="result.title"
					:url="result.url"
				/>
				<person-card
					v-else-if="postType === 'dc_person'"
					:email="result.email"
					:id="result.id"
					:image="result.image"
					:name="result.name"
					:phone="result.phone"
					:title="result.title"
				/>
				<service-card
					v-else-if="postType === 'dc_service'"
					:id="result.id"
					:label="result.label"
					:title="result.title"
					:url="result.url"
				/>
				<project-card
					v-else-if="postType === 'dc_project'"
					:excerpt="result.excerpt"
					:id="result.id"
					:image="result.image"
					:label="result.label"
					:suptitle="result.suptitle"
					:title="result.title"
					:url="result.url"
				/>
				<video-card
					v-else-if="postType === 'dc_video'"
					:id="result.id"
					:image="result.image"
					:title="result.title"
					:url="result.url"
				/>
				<webinar-card
					v-else-if="postType === 'dc_webinar'"
					:excerpt="result.excerpt"
					:id="result.id"
					:image="result.image"
					:title="result.title"
					:url="result.url"
				/>
				<post-card
					v-else-if="postType === 'dc_product'"
					:id="result.id"
					:image="result.image"
					:label="result.label"
					:title="result.title"
					:url="result.url"
				/>
				<post-card
					v-else
					:excerpt="result.excerpt"
					:id="result.id"
					:image="result.image"
					:label="result.label"
					:title="result.title"
					:url="result.url"
				/>
			</div>
		</div>

		<LoadMore
			v-if="showLoadMore && !endOfResults"
			:loading="loading"
			:currentPaged="currentPaged"
			:totalPages="totalPages"
			:totalPosts="totalPosts"
			:searchButtonId="searchButtonId"
			v-on:update-current-paged="updateCurrentPaged"
			@click.stop.prevent="loadMore"
		/>
	</div>
</template>

<script>
import { Translatable } from '@dc-post-grid/mixins/Translatable';
import {
	CourseCard,
	LocationCard,
	PersonCard,
	PostCard,
	ServiceCard,
	ProjectCard,
	VideoCard,
	WebinarCard,
} from './cards';
import { LoadMore, NoResults } from './ui';

export default {
	mixins: [Translatable],
	props: {
		loading: {
			type: Boolean,
			required: true,
		},
		results: {
			type: Array,
			required: true,
		},
		gridLayout: {
			type: String,
			default: 'default',
			required: false,
		},
		showLoadMore: {
			type: Boolean,
			default: false,
			required: true,
		},
		currentPaged: {
			type: Number,
			required: false,
		},
		totalPages: {
			type: Number,
			required: false,
		},
		totalPosts: {
			type: Number,
			required: false,
		},
		endOfResults: {
			type: Boolean,
			default: false,
			required: true,
		},
		postTypes: {
			type: Array,
			required: true,
		},
		searchButtonId: {
			type: String,
			required: true,
		},
	},
	computed: {
		gridClasses() {
			switch (this.postTypes[0]) {
				case 'dc_course':
					return 'small-12';
				case 'dc_course_overview':
					return 'small-12 medium-6 large-4 xlarge-3';
				case 'dc_product':
					return 'small-12 medium-6 large-4 xlarge-3';
					/*
				case 'dc_project':
					return 'small-12';
					*/
				case 'dc_video':
					return 'small-12 medium-6';
				case 'dc_webinar':
					return 'small-12 medium-6';
				default:
					return 'small-12 medium-6 large-4';
			}
		},
		postType() {
			return this.postTypes[0];
		},
	},
	methods: {
		updateCurrentPaged(newPagedNumber) {
			this.$emit('update-current-paged', newPagedNumber);
		},
		breakAfterMissingImage() {
			const i = this.results.findIndex((result) => result.image === undefined);
			this.$refs.resultsWrapperInnerAlt[i].insertAdjacentHTML('beforebegin', '<hr>');
		},
		addLocationCountryTitles() {
			const countryIndexes = this.results
				.map((res) => res.label.title) // Get all coutries from taxonomy.
				.map((country, index, labels) => labels.indexOf(country) === index && index) // Get the indexes of the first unqiue countries.
				.filter((country) => country !== false); // Remove empty data from prev map.

			// If the total post count does not match the original total post counts,
			// we will assume that the result is filtered, and therefore not show the site titles.
			if (this.$parent.initial_model.total_posts !== this.totalPosts) return;

			countryIndexes.forEach((i) => {
				//console.log('ORG :' + this.results[i].label.title);
				//console.log('TEXT: ' + this.dctxt(this.results[i].label.title));
				this.$refs.resultsWrapperInnerAlt[i].insertAdjacentHTML(
					'beforebegin',
					`<h2 class="cell location-country-title">${this.dctxt(this.results[i].label.title)}</h2>`
				);
			});
		},
		resetLocationCountryTitles() {
			document.querySelectorAll('.location-country-title').forEach((el) => {
				el.remove();
			});
		},
	},
	mounted() {
		if (this.postType === 'dc_person') {
			this.breakAfterMissingImage();
		}
		if (this.postType === 'dc_location') {
			this.addLocationCountryTitles();
		}
	},
	watch: {
		results: {
			handler: function() {
				this.$nextTick(() => {
					if (this.postType === 'dc_person') {
						this.breakAfterMissingImage();
					}
					if (this.postType === 'dc_location') {
						this.resetLocationCountryTitles();
						this.addLocationCountryTitles();
					}
				});
			},
		},
	},
	components: {
		CourseCard,
		LocationCard,
		PersonCard,
		PostCard,
		ServiceCard,
		ProjectCard,
		VideoCard,
		WebinarCard,
		LoadMore,
		NoResults,
	},
};
</script>
